/**
 * WHEP (WebRTC-HTTP Egress Protocol) client implementation with Trickle ICE support.
 *
 * WHEP is an IETF draft standard for consuming WebRTC streams via simple HTTP signaling.
 * Unlike the legacy protocol where the server generates the offer, in WHEP the client
 * generates the offer and sends it to the server, which responds with an answer.
 *
 * Trickle ICE (RFC 8840) allows sending ICE candidates incrementally via PATCH requests,
 * reducing initial connection latency by not waiting for full ICE gathering.
 *
 * Flow:
 * 1. Client creates RTCPeerConnection and SDP offer
 * 2. Client waits briefly (up to 60ms) for host candidates to be gathered
 * 3. Client POSTs offer with host candidates included to WHEP endpoint
 * 4. Server responds with SDP answer (including server ICE candidates)
 * 5. Client sets remote description, streaming can begin
 * 6. Any later candidates (STUN/TURN) are batched and sent via PATCH requests
 *
 * On local networks, host candidates in the offer are usually sufficient and no
 * PATCH requests are needed. STUN/TURN candidates are sent via trickle ICE only
 * when connectivity requires NAT traversal or relay.
 */

export type WhepConnectOptions = {
  /** ICE servers configuration */
  iceServers?: RTCIceServer[];
  /** Callback when ICE connection state changes */
  onIceConnectionStateChange?: (state: RTCIceConnectionState) => void;
  /** Callback when a track is received */
  onTrack?: (event: RTCTrackEvent) => void;
  /** Callback to terminate the session on the server (calls DELETE endpoint) */
  onDisconnect?: (sessionId: string) => Promise<void>;
};

export type WhepConnection = {
  /** The RTCPeerConnection instance */
  peerConnection: RTCPeerConnection;
  /** Session ID returned by the server */
  sessionId: string;
  /** Close the connection */
  close: () => void;
};

/**
 * Extract ICE credentials (ufrag and pwd) from SDP.
 */
function extractIceCredentials(sdp: string): {
  ufrag: string;
  pwd: string;
} | null {
  const ufragMatch = sdp.match(/a=ice-ufrag:(\S+)/);
  const pwdMatch = sdp.match(/a=ice-pwd:(\S+)/);

  if (!ufragMatch || !pwdMatch) {
    return null;
  }

  return {
    ufrag: ufragMatch[1],
    pwd: pwdMatch[1],
  };
}

/**
 * Format ICE candidates as a trickle-ice-sdpfrag (RFC 8840).
 * Groups candidates by their media line index (mid).
 */
function formatTrickleIceSdpfrag(
  iceUfrag: string,
  icePwd: string,
  candidates: RTCIceCandidate[]
): string {
  // Group candidates by mid
  const candidatesByMid = new Map<string, string[]>();
  for (const candidate of candidates) {
    const mid = candidate.sdpMid ?? '0';
    if (!candidatesByMid.has(mid)) {
      candidatesByMid.set(mid, []);
    }
    candidatesByMid.get(mid)!.push(candidate.candidate);
  }

  // Format according to RFC 8840 trickle-ice-sdpfrag
  const lines = [`a=ice-ufrag:${iceUfrag}`, `a=ice-pwd:${icePwd}`];

  // Add each m-line section with its candidates
  for (const [mid, candidateLines] of candidatesByMid) {
    lines.push(`m=video 9 UDP/TLS/RTP/SAVPF 96`);
    lines.push(`a=mid:${mid}`);
    for (const candidateLine of candidateLines) {
      lines.push(`a=${candidateLine}`);
    }
  }

  lines.push('');
  return lines.join('\r\n');
}

/**
 * Format end-of-candidates signal as a trickle-ice-sdpfrag.
 */
function formatEndOfCandidates(iceUfrag: string, icePwd: string): string {
  const lines = [
    `a=ice-ufrag:${iceUfrag}`,
    `a=ice-pwd:${icePwd}`,
    `m=video 9 UDP/TLS/RTP/SAVPF 96`,
    `a=mid:0`,
    `a=end-of-candidates`,
    '',
  ];

  return lines.join('\r\n');
}

/**
 * Connect to a WebRTC stream using the WHEP protocol with Trickle ICE support.
 *
 * @param whepEndpoint - Function that sends the SDP offer and returns the answer
 * @param patchEndpoint - Optional function to send trickle ICE candidates (PATCH)
 * @param options - Connection options
 * @returns WhepConnection object with the peer connection and cleanup function
 */
export async function connectWhep(
  whepEndpoint: (
    offerSdp: string
  ) => Promise<{ sessionId: string; answerSdp: string }>,
  patchEndpoint?: (sessionId: string, sdpfrag: string) => Promise<void>,
  options: WhepConnectOptions = {}
): Promise<WhepConnection> {
  const {
    iceServers = [],
    onIceConnectionStateChange,
    onTrack,
    onDisconnect,
  } = options;

  // Create peer connection
  const pc = new RTCPeerConnection({ iceServers });

  // Track session state for trickle ICE
  let sessionId: string | null = null;
  let iceCredentials: { ufrag: string; pwd: string } | null = null;
  const candidateBuffer: RTCIceCandidate[] = [];
  let closed = false;

  // Batching state for trickle ICE candidates
  const BATCH_DELAY_MS = 150; // Collect candidates for 150ms before sending
  let batchBuffer: RTCIceCandidate[] = [];
  let batchTimeout: ReturnType<typeof setTimeout> | null = null;

  function flushCandidateBatch() {
    if (batchTimeout) {
      clearTimeout(batchTimeout);
      batchTimeout = null;
    }

    if (
      batchBuffer.length === 0 ||
      !sessionId ||
      !iceCredentials ||
      !patchEndpoint ||
      closed
    ) {
      return;
    }

    const candidatesToSend = batchBuffer;
    batchBuffer = [];

    const sdpfrag = formatTrickleIceSdpfrag(
      iceCredentials.ufrag,
      iceCredentials.pwd,
      candidatesToSend
    );
    patchEndpoint(sessionId, sdpfrag).catch((error) => {
      console.warn(
        `WHEP: Failed to send ${candidatesToSend.length} ICE candidate(s):`,
        error
      );
    });
  }

  function queueCandidateForBatch(candidate: RTCIceCandidate) {
    batchBuffer.push(candidate);

    // If this is the first candidate in the batch, start the timer
    if (batchTimeout === null) {
      batchTimeout = setTimeout(flushCandidateBatch, BATCH_DELAY_MS);
    }
  }

  // Set up event handlers
  if (onIceConnectionStateChange) {
    pc.oniceconnectionstatechange = () => {
      onIceConnectionStateChange(pc.iceConnectionState);
    };
  }

  if (onTrack) {
    pc.ontrack = onTrack;
  }

  // Set up trickle ICE candidate handler
  if (patchEndpoint) {
    pc.onicecandidate = (event) => {
      if (closed) return;

      if (event.candidate) {
        // We have a candidate
        if (sessionId && iceCredentials) {
          // Session is established, queue candidate for batched sending
          queueCandidateForBatch(event.candidate);
        } else {
          // Buffer candidate until session is established
          candidateBuffer.push(event.candidate);
        }
      } else {
        // ICE gathering complete - flush any pending candidates first
        flushCandidateBatch();

        // Then send end-of-candidates signal
        if (sessionId && iceCredentials) {
          const sdpfrag = formatEndOfCandidates(
            iceCredentials.ufrag,
            iceCredentials.pwd
          );
          patchEndpoint(sessionId, sdpfrag).catch((error) => {
            console.warn('WHEP: Failed to send end-of-candidates:', error);
          });
        }
      }
    };
  }

  // Add receive-only transceiver for video
  // This tells the server we want to receive video
  const transceiver = pc.addTransceiver('video', { direction: 'recvonly' });

  // Try to set codec preferences to prioritize H.264 Baseline profile
  // which has the best compatibility
  if (transceiver.setCodecPreferences) {
    const capabilities = RTCRtpReceiver.getCapabilities('video');
    if (capabilities) {
      // Prioritize H.264 with baseline profile (42e01f)
      const h264Codecs = capabilities.codecs.filter(
        (codec) =>
          codec.mimeType === 'video/H264' &&
          codec.sdpFmtpLine?.includes('profile-level-id=42')
      );
      const otherCodecs = capabilities.codecs.filter(
        (codec) =>
          !(
            codec.mimeType === 'video/H264' &&
            codec.sdpFmtpLine?.includes('profile-level-id=42')
          )
      );
      const orderedCodecs = [...h264Codecs, ...otherCodecs];
      try {
        transceiver.setCodecPreferences(orderedCodecs);
      } catch (e) {
        console.warn('WHEP: Failed to set codec preferences:', e);
      }
    }
  }

  // Create offer
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  // Get base SDP and credentials
  const baseDescription = pc.localDescription;
  if (!baseDescription || !baseDescription.sdp) {
    pc.close();
    throw new Error('Failed to create local description');
  }

  iceCredentials = extractIceCredentials(baseDescription.sdp);

  // Wait briefly for host candidates to be gathered before sending offer.
  // Host candidates are discovered almost instantly (<10ms), so this short wait
  // allows them to be included directly in the offer SDP, reducing PATCH requests.
  // STUN/TURN candidates take longer and will still be sent via trickle ICE.
  //
  // We check up to 3 times at 20ms intervals, sending as soon as we have candidates.
  const CANDIDATE_CHECK_INTERVAL_MS = 20;
  const MAX_CHECKS = 3;
  for (let i = 0; i < MAX_CHECKS; i++) {
    await new Promise((resolve) => setTimeout(resolve, CANDIDATE_CHECK_INTERVAL_MS));
    if (candidateBuffer.length > 0) {
      break;
    }
  }

  // Build offer SDP with any candidates gathered during the wait.
  // Candidates in candidateBuffer were collected by onicecandidate handler.
  let offerSdp = baseDescription.sdp;
  if (candidateBuffer.length > 0) {
    // Group buffered candidates by mid and append to appropriate m-line sections
    const candidatesByMid = new Map<string, string[]>();
    for (const candidate of candidateBuffer) {
      const mid = candidate.sdpMid ?? '0';
      if (!candidatesByMid.has(mid)) {
        candidatesByMid.set(mid, []);
      }
      candidatesByMid.get(mid)!.push(`a=${candidate.candidate}`);
    }

    // Insert candidates after each corresponding a=mid line
    for (const [mid, candidateLines] of candidatesByMid) {
      const midPattern = new RegExp(`(a=mid:${mid}\\r\\n)`);
      offerSdp = offerSdp.replace(midPattern, `$1${candidateLines.join('\r\n')}\r\n`);
    }

    // Clear buffer since these candidates are now in the offer
    candidateBuffer.length = 0;
  }

  const localDescription = { type: baseDescription.type, sdp: offerSdp };

  // Send offer to WHEP endpoint immediately (don't wait for ICE gathering)
  // The server will wait for its ICE gathering and return candidates in the answer
  let response: { sessionId: string; answerSdp: string };
  try {
    response = await whepEndpoint(localDescription.sdp);
    sessionId = response.sessionId;
  } catch (error) {
    closed = true;
    if (batchTimeout) {
      clearTimeout(batchTimeout);
      batchTimeout = null;
    }
    pc.close();
    throw error;
  }

  // Set remote description (the answer from the server)
  try {
    await pc.setRemoteDescription({
      type: 'answer',
      sdp: response.answerSdp,
    });
  } catch (error) {
    closed = true;
    if (batchTimeout) {
      clearTimeout(batchTimeout);
      batchTimeout = null;
    }
    pc.close();
    throw error;
  }

  // Flush buffered ICE candidates now that session is established
  // Send all buffered candidates in a single batch
  if (patchEndpoint && iceCredentials && candidateBuffer.length > 0) {
    const sdpfrag = formatTrickleIceSdpfrag(
      iceCredentials.ufrag,
      iceCredentials.pwd,
      candidateBuffer
    );
    patchEndpoint(sessionId, sdpfrag).catch((error) => {
      console.warn(
        `WHEP: Failed to send ${candidateBuffer.length} buffered ICE candidate(s):`,
        error
      );
    });
    // Clear the buffer
    candidateBuffer.length = 0;
  }

  return {
    peerConnection: pc,
    sessionId: response.sessionId,
    close: () => {
      closed = true;

      // Cancel any pending batch timer
      if (batchTimeout) {
        clearTimeout(batchTimeout);
        batchTimeout = null;
      }

      pc.oniceconnectionstatechange = null;
      pc.ontrack = null;
      pc.onicecandidate = null;
      pc.close();

      // Notify server to terminate the session (fire-and-forget)
      if (onDisconnect && response.sessionId) {
        onDisconnect(response.sessionId).catch(() => {
          // Silently ignore - the connection is already closed locally
        });
      }
    },
  };
}
